import { useEffect, useRef, useState } from "react";
import {
  useTranslate,
  useGetIdentity,
  useDataProvider,
  useRefresh,
  usePermissions,
} from "react-admin";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { useQuery, useMutation } from "react-query";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CompanySwapper = () => {
  const t = useTranslate();
  const refresh = useRefresh();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  const { permissions, isLoading: permissionsLoading } = usePermissions();
  const dataProvider = useDataProvider();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    identity?.companyID
  );
  const lastSelectedCompanyIdRef = useRef(identity?.companyID);

  const canSwapCompany =
    !permissionsLoading &&
    (permissions?.administrator || permissions?.superadmin);
  const open = canSwapCompany && Boolean(anchorEl);

  const syncIdentityCompany = (companyID) => {
    if (!identity?.id) {
      return;
    }

    const nextIdentity = {
      id: identity.id,
      companyID,
      fullName: identity.fullName || "",
      avatar: identity.avatar || "",
    };

    try {
      localStorage.setItem("authIdentity", JSON.stringify(nextIdentity));
    } catch (e) {
      // ignore storage errors
    }

    try {
      window.dispatchEvent(
        new CustomEvent("authIdentityUpdated", { detail: nextIdentity })
      );
    } catch (e) {
      // ignore event errors
    }
  };

  const { data, isLoading } = useQuery(
    ["companies/all"],
    () => dataProvider.getListNoParams("companies/all"),
    {
      enabled: !!identity?.id,
    }
  );

  const { mutate } = useMutation(
    (companyID) =>
      dataProvider.update("users/arbitrary", {
        id: identity.id,
        data: { company_id: companyID },
      }),
    {
      onSuccess: (_, nextCompanyID) => {
        lastSelectedCompanyIdRef.current = nextCompanyID;
        syncIdentityCompany(nextCompanyID);
        setSelectedCompanyId(nextCompanyID);
        refresh();
      },
    }
  );

  // Keep local selected value in sync when the identity updates (e.g., on login)
  useEffect(() => {
    if (identity?.companyID) {
      setSelectedCompanyId(identity.companyID);
      lastSelectedCompanyIdRef.current = identity.companyID;
    }
  }, [identity?.companyID]);

  if (identityLoading || !identity || !identity.companyID || isLoading) {
    return null;
  }

  const sortedCompanies = Array.isArray(data)
    ? [...data].sort((a, b) =>
        a.name.localeCompare(b.name, "id", { sensitivity: "base" })
      )
    : [];

  const currentCompany =
    sortedCompanies.find((c) => c.id === selectedCompanyId)?.name ||
    identity.companyID;

  const handleChange = (_, index) => {
    if (!canSwapCompany || !sortedCompanies[index]) {
      return;
    }

    setAnchorEl(null);
    const nextCompanyId = sortedCompanies[index].id;
    lastSelectedCompanyIdRef.current = nextCompanyId;
    setSelectedCompanyId(nextCompanyId);
    mutate(nextCompanyId);
  };

  return (
    <>
      <Tooltip
        title={
          canSwapCompany
            ? t("mine.label.change_company")
            : "Swapper company hanya untuk superadmin atau administrator."
        }
        enterDelay={300}
      >
        <span>
          <Button
            endIcon={canSwapCompany ? <ExpandMoreIcon /> : null}
            variant="text"
            onClick={
              canSwapCompany ? (e) => setAnchorEl(e.currentTarget) : (e) => e.preventDefault()
            }
            color="inherit"
            sx={{
              color: "inherit",
              cursor: canSwapCompany ? "pointer" : "not-allowed",
            }}
            aria-disabled={!canSwapCompany}
          >
            {currentCompany}
          </Button>
        </span>
      </Tooltip>

      {canSwapCompany ? (
        <Menu open={open} onClose={() => setAnchorEl(null)} anchorEl={anchorEl}>
          {sortedCompanies.map((company, index) => (
            <MenuItem
              key={company.id}
              selected={company.id === selectedCompanyId}
              onClick={(e) => handleChange(e, index)}
            >
              {company.name}
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </>
  );
};

export default CompanySwapper;
